import { SESSION_TIMEOUT } from "../../constants/sessionTimeout";

/**
 * @name timeoutRules
 * @param {Object} timeoutRules
 * @description Utility functions used in Timeout behavior
 * @prop {number} Timeout.initTime session initial value minus SESSION_TIMEOUT.TIMER
 * @prop {number} Timeout.getElapseSecs Controls the countdown on TimeOut Modal
 * @prop {number} Timeout.getElapse Get the difference between Date.now() milliseconds and new Date()
 * @prop {number} Timeout.hasTime Check if we still have time before triggering the timeout modal
 */
const timeoutRules = {
  /**
   * @description
   * Reducing the countdown time from session timeout value
   * Since the final countdown modal is defaulted to count
   * to 5 minutes, we reduce it from the total session timeout.
   * @param {number} time timeout value in seconds
   * @returns {number} updated value
  */
  initTime(time) {
    return time - SESSION_TIMEOUT.TIMER;
  },
  /**
   * @description
   * Converts elapsed value to seconds -- i.e 1001 = 1 second, 2001 = 2 seconds, 3003 = 3 seconds and so on
   * @param {number} elapsed current elapsed seconds -- i.e 1002, 1003, 24000
   * @returns {number} updated elapsed seconds -- 1 second, 2 seconds, 24 seconds
  */
  getElapseSecs(elapsed) {
    return Math.round(elapsed / SESSION_TIMEOUT.UPDATE_DELAY);
  },
  /**
   * @description
   * Get the difference between Date.now() milliseconds and new Date()
   * @param {number} start -- @see Date.now()
   * @returns {number} elapsed value -- i.e 1002, 1003, 2400 and so on
  */
  getElapse(start) {
    return new Date() - start;
  },
  /**
   * @description
   * Check if we still have time before triggering the timeout modal
   * @param {number} time -- value in seconds
   * @returns {boolean} true/false if we have time remaining
  */
  hasTime(time) {
    return time > 0;
  }
};

/**
 * @name Timeout
 * @param {Object} Timeout
 * @description
 * Util that handles the timeout the global timeout session counter
 * init function will start the timer based on the session response or a custom timeout value (if client uses applyCustomTimeout function)
 * when timeout is over, _timeOut function will run the callback provided by the client
 * @prop {number} Timeout.initialValue session initial value -- i.e 1200 seconds
 * @prop {number} Timeout.customTimeout Controls the countdown on TimeOut Modal
 * @prop {number} Timeout.sessionSecs holds session secs minus SESSION_TIMEOUT.TIMER
 * @prop {number} Timeout.sessionTimer holds setInterval function responsible for counting
 * @prop {number} Timeout.sessionElapsedSecs how many seconds elapsed
 * @prop {number} Timeout.sessionStart @see Date.now()
 * @prop {function} Timeout.callbackFn Timeout callback function
 */
export const Timeout = {
  initialValue: 0,
  customTimeout: null,
  sessionSecs: null,
  sessionTimer: null,
  sessionElapsedSecs: 0,
  sessionStart: null,
  callbackFn: null,
  /**
  * @description sets a custom timeout which may vary -- 1 minute, 2 minutes and so on
  * @param {number} - value in seconds -- i.e 300 corresponds to 5 minutes
  * @return {void}
  */
  setCustomTimeout(customTimeout) {
    // Ensure that the requestedTimeout from the client is not exceeding the GMA timeout
    // Some brands might have flows with specific timeouts like CheckIn kiosk (enterprise web) or CDA (national web)
    const newTimeout =
      customTimeout > SESSION_TIMEOUT.GMA_TIMEOUT ? SESSION_TIMEOUT.GMA_TIMEOUT : customTimeout;
    Timeout.customTimeout = newTimeout;
  },
  /**
  * @description init the session behavior based on session timeout
  * @param {number} - sessionTimeoutFromResponse - timeout value in seconds
  * @param {function} - callback to be triggered once timeout is triggered
  * @return {void}
  */
  init(sessionTimeoutFromResponse, callback) {
    Timeout.callbackFn = callback;
    const sessionTimeout = Timeout.customTimeout ? Timeout.customTimeout : sessionTimeoutFromResponse;
    Timeout.initialValue = sessionTimeout;

    // Clear previous session interval (tick) if init was called again
    if (Timeout.sessionTimer) clearInterval(Timeout.sessionTimer);

    // Reducing the countdown time from session timeout value
    //  Since the final countdown modal is defaulted to count
    //  to 5 minutes, we reduce it from the total session timeout.
    const timeoutToModal = timeoutRules.initTime(sessionTimeout);
    const hasTime = timeoutRules.hasTime(timeoutToModal);

    Timeout.tickCallback('init()', {sessionTimeoutFromResponse});
    
    if (hasTime) {
      Timeout.sessionSecs = timeoutToModal
      Timeout.sessionStart = Date.now();
      Timeout.sessionElapsedSecs = 0;
      Timeout.sessionTimer = setInterval(
        Timeout._tick,
        SESSION_TIMEOUT.UPDATE_DELAY // 1000
      );

      return;
    }

    Timeout._timeOut(sessionTimeout);
  },
  /**
  * @description stop session timeout timer
  * @return {void}
  */
  _stopTimer() {
    clearInterval(Timeout.sessionTimer);
  },
  /**
  * @description tick function triggered based on SESSION_TIMEOUT.UPDATE_DELAY value
  * if elapsed seconds greater than sessionSecs we trigger _timeOut function
  * @return {void}
  */
  _tick() {
    const elapsed = timeoutRules.getElapse(Timeout.sessionStart);
    Timeout.sessionElapsedSecs = timeoutRules.getElapseSecs(elapsed);
    Timeout.sessionElapsedSecs >= Timeout.sessionSecs && Timeout._timeOut();
    Timeout.tickCallback('_tick()', {
      elapsedSecsTarget: Timeout.sessionSecs,
      initialValue: Timeout.initialValue,
      elapsedSecs: Timeout.sessionElapsedSecs
    })
  },
  /**
   * @description Sets the tick callback function that will be passed the arguments from the Timeout.tickCallback(...)
   * @param      {Function}  callback  The callback function
   * @returns {void} just sets the internal `Timeout._tickCallback`
   * @example
   *   utils.setTickCallback(console.log);
   *   Timeout.tickCallback({thing:true})
   *   // logs {thing:true}
   */
  setTickCallback(callback) {
    Timeout._tickCallback = callback;
  },
  /**
   * @description Runs the `Timeout._tickCallback` that was passed in via {@link Timeout.setTickCallback}
   * @param      {any}  args    The arguments to send to the function
   * @return     {any}  the set `Timeout._tickCallback(...args)`
   * @example
   *   Timeout.tickCallback({thing:false});    // nothing
   *   Timeout.setTicketCallback(console.log); // set the callback
   *   Timeout.ticketCallback({thing:true});   // logs {thing.true}
   */
  tickCallback: (...args) => Timeout._tickCallback ? Timeout._tickCallback(...args) : () => {},
  /**
  * @description Cancels a `Timeout` object created by `setInterval()
  * and call Timeout callback function
  * @return {void}
  */
  _timeOut(remainingTime = this._getRemainingSessionSeconds()) {
    Timeout.tickCallback('_timeOut()', {remainingTime});
    clearInterval(Timeout.sessionTimer);
    Timeout.callbackFn(remainingTime);
  },
  /**
  * @description return how many seconds until session expires based on initial value and elapsed seconds
  * @return {number} number of seconds
  */
  _getRemainingSessionSeconds() {
    return Timeout.initialValue - Timeout.sessionElapsedSecs;
  }
};
